import React, { ChangeEvent, RefObject, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, ClockIcon, FileUpIcon, ShieldCheckIcon } from 'lucide-react';
import { CarliChat } from './CarliChat';
import { activePrompts, activityFor, attentionFor, onboardingPrompts, recommendationFor } from '../data/homeSignals';
export type PortalView = 'carli' | 'inventory' | 'performance' | 'marketing' | 'controls' | 'messages' | 'finances' | 'demand' | 'pipeline';
type QuickStats = {
  received: string;
  sold: string;
  sales: string;
  live: string;
  liveStyles?: string;
  maxOpportunity: string;
  msrp?: string;
  salesDelta?: string | null;
  unitsDelta?: string | null;
  forecastSales?: string | null;
  forecastDetail?: string | null;
};
type HomeDashboardProps = {
  selectedOwner: string;
  quickStats: QuickStats;
  hasInventory: boolean;
  shopifyConnected: boolean;
  onConnectShopify: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  onRunAnalysis: () => void;
  onNavigate: (view: PortalView) => void;
  latestReport?: {
    name: string;
    onOpen: () => void;
  };
};
export function HomeDashboard({
  selectedOwner,
  quickStats,
  hasInventory,
  fileInputRef,
  onFileChange,
  fileName,
  onRunAnalysis,
  onNavigate,
  latestReport
}: HomeDashboardProps) {
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const attention = hasInventory ? attentionFor(selectedOwner) : [];
  const recommendation = hasInventory ? recommendationFor(selectedOwner) : null;
  const activity = hasInventory ? activityFor(selectedOwner) : [];
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" data-id="element-932" data-mp="zofq2o54hkvy">
      {!hasInventory && <section className="pb-8" aria-labelledby="home-heading" data-id="element-933" data-mp="3pdrjbdxe4sz1">
          <CarliLabel data-id="element-934" data-mp="3pf2kd5zgbmmg" />
          <h1 id="home-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-935" data-mp="3pbesscdkhaad">Welcome to Carli</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink/65" data-id="element-936" data-mp="3lvbt60rdtkwj">
            Start by adding inventory. Carli will evaluate the opportunity and recommend the best recovery path.
          </p>
        </section>}

      {hasInventory && <section aria-labelledby="snapshot-heading" data-id="element-937" data-mp="3cx9n36ssk22e">
          <div className="flex items-center gap-3" data-id="element-938" data-mp="3ht747fbvpcg8">
            <h2 id="snapshot-heading" className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-939" data-mp="3hw34vbf42f8d">Your recovery snapshot</h2>
            <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-940" data-mp="37wc62xjipe2h" />
            {latestReport && <button type="button" onClick={latestReport.onOpen} className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-forest hover:underline" data-id="element-941" data-mp="2u5pb9ggvo8bo">
                Latest report: {latestReport.name}
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-942" data-mp="2k79sc0s085d9" />
              </button>}
          </div>

          <dl className="mt-6 grid grid-cols-2 gap-x-8 gap-y-7 lg:grid-cols-4" data-id="element-943" data-mp="2p7ffp4r8r5pj">
            <HeadlineStat label="Total sales" value={quickStats.sales} detail={quickStats.salesDelta ?? 'Gross recovery revenue'} data-id="element-944" data-mp="25lvlb2lqzdio" />
            <HeadlineStat label="Units sold" value={quickStats.sold} detail={quickStats.unitsDelta ?? 'Across all recovery channels'} data-id="element-945" data-mp="2fhp8rh108rf8" />
            <HeadlineStat label="Live units" value={quickStats.live} detail={`Across ${quickStats.liveStyles ?? '0'} styles`} data-id="element-946" data-mp="2ai2tl21m913s" />
            <HeadlineStat label="Est. next 7 day sales" value={quickStats.forecastSales ?? '—'} detail={quickStats.forecastDetail ?? 'Projected recovery sales'} data-id="element-947" data-mp="2ogkdeooehtgf" />
          </dl>
        </section>}

      <section className="mt-10" aria-labelledby="add-inventory-heading" data-id="element-948" data-mp="2t93qzln38ol8">
        <div className="flex items-center gap-3" data-id="element-949" data-mp="378vbm5841387">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-950" data-mp="379o88ddcuccm">Add inventory</span>
          <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-951" data-mp="339e4l7d1by0x" />
        </div>

        <article className="mt-4 rounded-3xl border border-ink/10 bg-paper p-6 shadow-sm sm:p-8" data-id="element-952" data-mp="10kcrmof94k2q">
          <h2 id="add-inventory-heading" className="font-display text-2xl font-medium tracking-tight sm:text-3xl" data-id="element-953" data-mp="15ie9x74usfd5">
            {hasInventory ? 'Keep your recovery growing' : 'Send Carli your first inventory'}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-954" data-mp="1fblggvbby1et">
            {hasInventory ? `Your current inventory is projected to generate ${quickStats.forecastSales ?? 'additional sales'} over the next 7 days. Upload a spreadsheet and Carli estimates recovery, likely channels, and expected timing before you confirm the upload.` : 'Upload a spreadsheet and Carli estimates recovery, likely channels, and expected timing. Review her report before you confirm the upload.'}
          </p>

          <ol className="mt-6 grid gap-3 sm:grid-cols-3" data-id="element-955" data-mp="270f2j9oey8wb">
            <UploadStep number="1" title="Choose how to connect" text="Shopify, a CSV upload, or your 3PL." data-id="element-956" data-mp="2731avbj4osfl" />
            <UploadStep number="2" title="Carli analyzes it" text="She estimates recovery value, channel fit, and expected timing." data-id="element-957" data-mp="3rktofgw2mrwp" />
            <UploadStep number="3" title="Review, then confirm" text="Top findings first. Nothing goes live until you approve it." data-id="element-958" data-mp="3ipvtk49d552q" />
          </ol>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-959" data-mp="3dphvnf1h7bif">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="sr-only" aria-label="Upload inventory file" data-id="element-960" data-mp="38tkhnfdoqh8t" />
            <button type="button" onClick={() => {
            setAnalysisStarted(true);
            onRunAnalysis();
          }} className={`inline-flex items-center justify-center gap-2 rounded-full border px-5 py-3.5 text-sm font-semibold transition-colors ${analysisStarted ? 'border-[#BB5E40]/35 bg-paper text-[#94452E] hover:bg-cream' : 'border-[#BB5E40] bg-[#BB5E40] text-cream hover:bg-[#94452E]'}`} data-id="element-961" data-mp="2zrtb68bbm6dw">
              <FileUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-962" data-mp="1gxi25kz2x86s" />
              Add inventory &amp; run Carli analysis
            </button>
          </div>

          {fileName && <div className="mt-4 flex items-center gap-2 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-ink/70" role="status" data-id="element-963" data-mp="183ljig54idw4">
              <CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-964" data-mp="1vruwcskczrb4" />
              <span data-id="element-965" data-mp="1ztgiz5mp3jyi"><strong className="font-semibold text-ink" data-id="element-966" data-mp="1ztggmyz837vc">{fileName}</strong> is ready to add to your recovery workspace.</span>
            </div>}

          <p className="mt-6 flex items-start gap-2 border-t border-ink/10 pt-5 text-xs leading-relaxed text-ink/50" data-id="element-967" data-mp="1qtthykxvog5z">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" aria-hidden="true" data-id="element-968" data-mp="af1o03l330uk" />
            Nothing is added to your live inventory or activated without your approval. ERP and 3PL connections are coming next.
          </p>
        </article>
      </section>

      <section className="mt-10" aria-labelledby="meet-carli-heading" data-id="element-969" data-mp="2w24zz1jbbbq">
        <div className="flex items-center gap-3" data-id="element-970" data-mp="gwmmmrvpr13n">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-971" data-mp="gwcy78qbh202">Your recovery analyst</span>
          <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-972" data-mp="bvp4ugem892o" />
        </div>
        <div className="mt-4 grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-stretch" data-id="element-973" data-mp="3kdv2x7aslsw9">
          <article className="flex flex-col justify-between p-1 sm:p-2" data-id="element-974" data-mp="3gdl0g4m7lkm5">
            <div data-id="element-975" data-mp="3u4gb9mg9qbsd">
              <CarliPulse data-id="element-976" data-mp="3u4zhw70gxdr3" />
              <h2 id="meet-carli-heading" className="mt-5 font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-977" data-mp="3u5hjlj5wgk6e">Meet Carli</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink/65" data-id="element-978" data-mp="31mlr2rdjus1i">
                Carli watches every unit you send, values it against live secondary-market demand, and tells you what to do next. Ask her anything — she answers from your own numbers.
              </p>
            </div>
            <ul className="mt-6 space-y-2.5 text-sm text-ink/70" data-id="element-979" data-mp="2g3iv77lcehww">
              <CarliSkill text="Forecasts what your inventory will recover, and how fast" data-id="element-980" data-mp="2g5vebo2u6ybj" />
              <CarliSkill text="Prices and routes units across approved channels" data-id="element-981" data-mp="2l1kg7y2bwrb0" />
              <CarliSkill text="Flags what needs your approval before anything goes live" data-id="element-982" data-mp="2q3jljdhmpyal" />
            </ul>
          </article>

          <div className="overflow-hidden rounded-3xl shadow-sm" data-id="element-983" data-mp="2uz8nfnh4fra2">
            <CarliChat prompts={hasInventory ? activePrompts : onboardingPrompts} subheading="Ask about your inventory, recovery strategy, performance, or what to do next." context={carliContext(selectedOwner, quickStats, hasInventory)} data-id="element-984" data-mp="2z1byiazvotut" />
          </div>
        </div>
      </section>

      {hasInventory ? <>
          <AttentionSection items={attention} liveUnits={quickStats.live} onNavigate={onNavigate} data-id="element-985" data-mp="3ipum4o58jz7e" />

          {recommendation && <section className="mt-6 overflow-hidden rounded-3xl bg-ink text-cream shadow-sm" aria-labelledby="recommendation-heading" data-id="element-986" data-mp="174lo0pk4uuj5">
              <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[1.4fr_0.6fr] lg:items-end" data-id="element-987" data-mp="x54g01hes4k0">
                <div data-id="element-988" data-mp="s61eus4juihb">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cream/55" data-id="element-989" data-mp="s6jlmiou8f3h">
                    <CarliGlyph tone="cream" data-id="element-990" data-mp="1pt8r52ir19u3" />
                    Carli recommends
                  </span>
                  <h2 id="recommendation-heading" className="mt-4 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-991" data-mp="1kv764c1rj0fz">
                    {recommendation.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/65" data-id="element-992" data-mp="1b4m2293bl3po">{recommendation.observation}</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/75" data-id="element-993" data-mp="28m4bt72yt1is">{recommendation.reasoning}</p>
                </div>
                <div className="lg:text-right" data-id="element-994" data-mp="fp6zze2kr5h2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cream/50" data-id="element-995" data-mp="fs1kf5ll8qah">Expected impact</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-996" data-mp="po4wp4a5m5b5">{recommendation.impact}</p>
                  <button type="button" onClick={() => onNavigate(recommendation.target)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#BB5E40] px-5 py-3 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-997" data-mp="kpbgh43hg07a">
                    {recommendation.action}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-998" data-mp="18gqlj4mn8vm8" />
                  </button>
                </div>
              </div>
            </section>}
        </> : <section className="mt-8" aria-labelledby="getting-started-heading" data-id="element-999" data-mp="1iapog5ujkjf8">
          <h2 id="getting-started-heading" className="sr-only" data-id="element-1000" data-mp="1dfadblnx51mi">Getting started</h2>
          <ol className="grid gap-4 lg:grid-cols-3" data-id="element-1001" data-mp="1x0k7l7enl69y">
            <Step number="1" title="Add inventory" text="Upload a spreadsheet or connect Shopify." data-id="element-1002" data-mp="21145weayqfxo" />
            <Step number="2" title="Set your recovery rules" text="Choose approved channels, pricing boundaries, and timing." data-id="element-1003" data-mp="1n1bj2u2k4juz" />
            <Step number="3" title="Review Carli’s recommendation" text="Carli will estimate recovery and recommend a recovery strategy." data-id="element-1004" data-mp="1rzmsj3oxwsco" />
          </ol>
        </section>}

      {activity.length > 0 && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="activity-heading" data-id="element-1005" data-mp="1i39yu5teaehx">
          <div className="flex items-center justify-between gap-4" data-id="element-1006" data-mp="14coh4kfogalm">
            <h2 id="activity-heading" className="flex items-center gap-2 text-base font-semibold" data-id="element-1007" data-mp="19azqyv5z2l3u">
              <ClockIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1008" data-mp="udp6wxf9k6wi" />
              Recent activity
            </h2>
            <button type="button" onClick={() => onNavigate('performance')} className="text-sm font-medium text-forest hover:underline" data-id="element-1009" data-mp="zaz2kut8xess">
              View all activity
            </button>
          </div>
          <ul className="mt-4 divide-y divide-ink/10" data-id="element-1010" data-mp="bf425q67noft">
            {activity.map(entry => <li key={`${entry.when}-${entry.detail}`} className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-baseline sm:gap-6" data-id="element-1011" data-mp="6hlstyoa777a">
                <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-1012" data-mp="z5q2ev9z44f9">{entry.when}</span>
                <span className="text-sm text-ink/75" data-id="element-1013" data-mp="u7yai19bwib2">{entry.detail}</span>
              </li>)}
          </ul>
        </section>}
    </div>;
}
function AttentionSection({
  items,
  liveUnits,
  onNavigate
}: {
  items: ReturnType<typeof attentionFor>;
  liveUnits: string;
  onNavigate: (view: PortalView) => void;
}) {
  if (items.length === 0) {
    return <section className="mt-8 rounded-3xl border border-forest/20 bg-forest/5 p-5 sm:p-7" aria-labelledby="attention-heading" data-id="element-1014" data-mp="34z7vw4nj8ir3">
        <div className="flex items-start gap-3" data-id="element-1015" data-mp="1khypckypbn98">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-forest" aria-hidden="true" data-id="element-1016" data-mp="1kf2qmuf14ujq" />
          <div data-id="element-1017" data-mp="1pdxgggbezjdc">
            <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-1018" data-mp="1uf3fevvo42vb">You’re all caught up</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-1019" data-mp="1z5tjm5scxzof">
              Carli is actively managing {liveUnits} units across your approved recovery channels.
            </p>
          </div>
        </div>
      </section>;
  }
  return <section className="mt-8" aria-labelledby="attention-heading" data-id="element-1020" data-mp="2856soxckkm8m">
      <div className="flex items-center gap-2.5" data-id="element-1021" data-mp="2d53076kh6vsx">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#BB5E40]" data-id="element-1022" data-mp="2d6e0gwepplfa" />
        <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-1023" data-mp="2i3cxpntoa8hp">Needs your attention</h2>
        <span className="rounded-full bg-[#BB5E40]/12 px-2.5 py-0.5 text-xs font-semibold text-[#94452E]" data-id="element-1024" data-mp="isv964b9yz2f">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <ul className="mt-3 space-y-3" data-id="element-1025" data-mp="2e6gvcr7t1vq4">
        {items.map(item => <li key={item.id} data-id="element-1026" data-mp="299702uxqipud">
            <article className="flex flex-col gap-4 rounded-2xl border border-[#BB5E40]/25 bg-paper p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-id="element-1027" data-mp="297vviswgte27">
              <div data-id="element-1028" data-mp="2j74obiflgmmz">
                <h3 className="text-base font-semibold" data-id="element-1029" data-mp="2j7whynpmsaam">{item.title}</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-1030" data-mp="1vh0svyck7t72">{item.detail}</p>
                {item.impact && <p className="mt-2 text-sm font-semibold text-forest" data-id="element-1031" data-mp="1qiyzoklf6dhv">{item.impact}</p>}
              </div>
              <button type="button" onClick={() => onNavigate(item.target)} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1032" data-mp="249ufkguc5uuy">
                {item.action}
              </button>
            </article>
          </li>)}
      </ul>
    </section>;
}
function HeadlineStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <div data-id="element-1033" data-mp="12gaua4d8gxcx">
      <dt className="text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-1034" data-mp="nplh4wg7p86f">{label}</dt>
      <dd data-id="element-1035" data-mp="nnqvffx49c1a">
        <p className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-id="element-1036" data-mp="soetgljrit50">{value}</p>
        <p className="mt-1.5 text-[13px] text-ink/55" data-id="element-1037" data-mp="1gdpodmefltb8">{detail}</p>
      </dd>
    </div>;
}
function Step({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" data-id="element-1038" data-mp="16gbfq8yr28th">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#BB5E40]/12 font-display text-sm font-semibold text-[#94452E]" data-id="element-1039" data-mp="16ifiuootr3sp">{number}</span>
      <h3 className="mt-4 text-base font-semibold" data-id="element-1040" data-mp="3r5isv9u0uo6v">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-ink/65" data-id="element-1041" data-mp="3w551f5ywng9c">{text}</p>
    </li>;
}
function UploadStep({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-cream/40 p-4" data-id="element-1042" data-mp="1xntksm80cvms">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#BB5E40] text-xs font-semibold text-cream" data-id="element-1043" data-mp="1xpo45w3msfor">{number}</span>
      <p className="mt-3 text-sm font-semibold" data-id="element-1044" data-mp="1nya15ech0xqv">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-1045" data-mp="1nvnstchrae7l">{text}</p>
    </li>;
}
function CarliPulse() {
  const reduceMotion = useReducedMotion();
  return <div className="relative flex h-24 w-24 items-center justify-center" aria-hidden="true" data-id="element-1046" data-mp="1ewaikhlt5llt">
      {!reduceMotion && [0, 1].map(ring => <motion.span key={ring} className="absolute inset-0 rounded-full border border-[#BB5E40]/35" initial={{
      scale: 0.7,
      opacity: 0.5
    }} animate={{
      scale: 1.15,
      opacity: 0
    }} transition={{
      duration: 2.6,
      repeat: Infinity,
      delay: ring * 1.3,
      ease: 'easeOut'
    }} data-id="element-1047" data-mp="19zts3gr4yv5k" />)}
      <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[#BB5E40]/25 bg-paper" data-id="element-1048" data-mp="36wzgbuv2s3zq">
        <span className="h-9 w-9 bg-[#BB5E40]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-1049" data-mp="3bu81837lym4o" />
      </span>
    </div>;
}
function CarliSkill({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-2.5" data-id="element-1050" data-mp="32si2ecbeckwv">
      <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1051" data-mp="d4he3pob8qmh" />
      <span className="leading-relaxed" data-id="element-1052" data-mp="i3kjjb87cyuz">{text}</span>
    </li>;
}
function CarliLabel() {
  return <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1053" data-mp="139pn4xz035kr">
      <CarliGlyph data-id="element-1054" data-mp="yb60evcl1prc" />
      Carli
    </span>;
}
function CarliGlyph({
  tone = 'coral'
}: {
  tone?: 'coral' | 'cream';
}) {
  return <span aria-hidden="true" className={`h-4 w-4 shrink-0 ${tone === 'cream' ? 'bg-cream' : 'bg-[#BB5E40]'}`} style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1055" data-mp="1d0am4mil6c47" />;
}
function carliContext(brand: string, stats: QuickStats, hasInventory: boolean) {
  if (!hasInventory) {
    return 'The brand is signed into the Manymoons recovery portal but has not added inventory yet. Help them understand what to send first, what data Carli needs, how recovery pricing works, and which channels might fit. Keep answers to two short paragraphs at most.';
  }
  return [`The brand is ${brand || 'a Manymoons brand'}, signed into their recovery portal.`, `Current position: ${stats.live} active units, ${stats.sold} units sold, ${stats.sales} recovery generated, ${stats.maxOpportunity} of remaining opportunity across ${stats.received} units received.`, 'Answer as their recovery analyst using these numbers. Be specific and commercial. Two short paragraphs at most.'].join(' ');
}